import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  StartRunParams,
  supersetRecommendationService,
} from '../services/supersetRecommendation.service';

export function useSupersetRecommendationRuns() {
  return useQuery({
    queryKey: ['superset-recommendation-runs'],
    queryFn: () => supersetRecommendationService.listRuns(),
    staleTime: 15 * 1000,
  });
}

export function useSupersetRecommendationRun(
  runId: string | null,
  options?: { isRunning?: boolean },
) {
  return useQuery({
    queryKey: ['superset-recommendation-run', runId],
    queryFn: () => supersetRecommendationService.getRun(runId as string),
    enabled: !!runId,
    // Poll while the background job is still working.
    refetchInterval: options?.isRunning ? 3000 : false,
    staleTime: 15 * 1000,
  });
}

export function useActiveSupersets() {
  return useQuery({
    queryKey: ['active-supersets'],
    queryFn: () => supersetRecommendationService.listActiveSupersets(),
    staleTime: 15 * 1000,
  });
}

export function useStartSupersetRecommendationRun() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: StartRunParams) =>
      supersetRecommendationService.startRun(params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['superset-recommendation-runs'] });
    },
  });
}

export function useAcceptSupersetRecommendation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (recommendationId: string) =>
      supersetRecommendationService.accept(recommendationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['superset-recommendation-run'] });
      // Accepting creates a catalog row → refresh the Active Supersets list.
      queryClient.invalidateQueries({ queryKey: ['active-supersets'] });
    },
  });
}

export function useDismissSupersetRecommendation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (recommendationId: string) =>
      supersetRecommendationService.dismiss(recommendationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['superset-recommendation-run'] });
      // Dismissing an accepted rec removes its catalog row → refresh the list.
      queryClient.invalidateQueries({ queryKey: ['active-supersets'] });
    },
  });
}

export function useDeleteSupersetRecommendation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (recommendationId: string) =>
      supersetRecommendationService.delete(recommendationId),
    onSuccess: () => {
      // Hard delete removes the catalog row, the backing node, and the rec
      // record → refresh both the active list and the run views.
      queryClient.invalidateQueries({ queryKey: ['active-supersets'] });
      queryClient.invalidateQueries({ queryKey: ['superset-recommendation-run'] });
      queryClient.invalidateQueries({ queryKey: ['superset-recommendation-runs'] });
    },
  });
}
